import * as THREE from 'three'
import { Vector3 } from 'three'
import { CommonCameraPositions } from 'Modules/types/pages/imageReview/waypointVisualizertypes'
import type { Object3D } from 'three'

class PerspectiveCamera extends THREE.PerspectiveCamera {
  /**
   * Sets the position on the camera based on the position of the object being passed in
   * @param object ThreeJS Object3D Object
   */
  setCameraPositionByObjectPosition(object: Object3D): void {
    const objectWorldPosition = new THREE.Vector3()
    const offset = new THREE.Vector3(0, 5, 10)
    object.getWorldPosition(objectWorldPosition)
    const newCameraPosition = new Vector3().addVectors(objectWorldPosition.clone(), offset)

    this.position.set(newCameraPosition.x, newCameraPosition.y, newCameraPosition.z)
    this.lookAt(objectWorldPosition.x, objectWorldPosition.y, objectWorldPosition.z)
  }

  /**
   *
   * @param position Common camera position (i.e. Front, Back, Left, Right)
   * @param target The 3D object which the camera is positioned in relation to
   * @param controls The controls for the 3D scene; needed to adjust control target
   * @param offset Vertical offset for camera position and control target
   */
  setCommonCameraPosition(position: CommonCameraPositions, target: Object3D, controls, offset = 15, lookAtObject = true): void {
    const boundingBox = new THREE.Box3().setFromObject(target)
    const objectSize = new Vector3()
    const positionVector = new Vector3()
    const scaledY = objectSize.y * 0.25

    boundingBox.getSize(objectSize)

    switch (position) {
      case 'front':
        positionVector.set(0, scaledY, 15)
        break
      case 'left':
        positionVector.set(-(objectSize.x / 2 + offset), scaledY, 0 - objectSize.z / 2)
        break
      case 'back':
        positionVector.set(0, scaledY, 0 - objectSize.z - offset)
        break
      case 'right':
        positionVector.set(objectSize.x / 2 + offset, scaledY, 0 - objectSize.z / 2)
        break
      case 'top':
        positionVector.set(0, offset, offset / 3)
        break
      default:
        break
    }

    if (controls) controls.target.set(0, 0, 0 + objectSize.z / 2)

    this.position.set(positionVector.x, positionVector.y, positionVector.z)
    if (lookAtObject) this.lookAt(0, 0, 0 + objectSize.z / 2)
  }

  /**
   *  Fit cameras view on a list of 3d objects
   * 
   * @param selection an array of Object3Ds to focus on
   * @param controls The controls for the 3D scene; needed to adjust control target
   * @param options.centerOffset offset the camera's focal point or offset from the selections center point
   * @param options.fitOffset margin between 3d objects and viewport edge
   */
  fitCameraToSelection(selection: Array<Object3D>, controls, options: { centerOffset?: THREE.Vector3, fitOffset: number } = {
    centerOffset: new THREE.Vector3(1, 1, 1),
    fitOffset: 1
  }) {
    const box = new THREE.Box3();
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    box.makeEmpty();
    for (const object of selection) {
      box.expandByObject(object);
    }

    box.getSize(size);
    box.getCenter(center);
    center.x = center.x * options?.centerOffset.x
    center.y = center.y * options?.centerOffset.y
    center.z = center.z * options?.centerOffset.z

    const maxSize = Math.max(size.x, size.y, size.z);
    const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * this.fov / 360));
    const fitWidthDistance = fitHeightDistance / this.aspect;
    const distance = options.fitOffset * Math.max(fitHeightDistance, fitWidthDistance);

    const direction = controls.target.clone()
      .sub(this.position)
      .normalize()
      .multiplyScalar(distance);

    controls.maxDistance = distance * 10;
    controls.target.copy(center);

    this.near = distance / 100;
    this.far = distance * 100;
    this.updateProjectionMatrix();

    this.position.copy(controls.target).sub(direction);

    controls.update();
  }

}



export { PerspectiveCamera }
